'use client';

import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MaskedInput, MASK_PRESETS } from '@/components/ui/masked-input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Show year/month dropdowns for easier navigation
   * Useful for date of birth, etc.
   */
  yearNavigation?: boolean;
  /**
   * Minimum date that can be selected
   * Dates before this will be disabled
   */
  minDate?: Date;
  /**
   * Maximum date that can be selected
   * Dates after this will be disabled
   */
  maxDate?: Date;
  /**
   * Allow manual text input in DD/MM/YYYY format
   * When true, renders an input field with calendar icon
   */
  allowInput?: boolean;
  /**
   * An accessible name for the typed input on the `allowInput` arm (VALE-1321).
   *
   * The masked input has no visible label of its own when this picker is
   * rendered bare inside a cell, which is how the attribute grid's `date` row
   * renders it, where every sibling editor passes `aria-label={field.label}`.
   * Forwarded to `MaskedInput` only; the button arm already names itself with
   * the formatted date or the placeholder.
   */
  ariaLabel?: string;
  /**
   * Offer a visible way to clear the date, on the button arm (VALE-1854).
   *
   * **Opt-in, because for most callers the field is required** and a clear
   * control there would offer a state the form then rejects. Where the date is
   * genuinely optional the opposite was true: the button arm had no clear
   * affordance at all, so a date that had been set could only be swapped for a
   * different one, never removed. Reported on the hand-over dialog, whose
   * "Expected to be finished by" is labelled `(optional)` and placeholdered "No
   * date set" — both promising an empty state the reader could not get back to.
   *
   * react-day-picker does deselect on clicking the selected day, so the state
   * was reachable; nothing on screen said so, which makes it a trick rather than
   * an affordance.
   */
  clearable?: boolean;
}

/**
 * Format date to DDMMYYYY (raw, no separators) for masked input
 */
function formatDateRaw(date: Date | undefined): string {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${day}${month}${year}`;
}

/**
 * Parse DDMMYYYY raw string to Date
 * Returns undefined if invalid
 */
function parseRawDate(raw: string): Date | undefined {
  if (raw.length !== 8) return undefined;

  const day = parseInt(raw.slice(0, 2), 10);
  const month = parseInt(raw.slice(2, 4), 10);
  const year = parseInt(raw.slice(4, 8), 10);

  const date = new Date(year, month - 1, day);

  // Validate the date is real (e.g., not 31/02/2024)
  if (
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return undefined;
  }

  return date;
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  yearNavigation = false,
  minDate,
  maxDate,
  allowInput = false,
  ariaLabel,
  clearable = false
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<{
    syncedFor: Date | undefined;
    month: Date | undefined;
    rawValue: string;
  }>(() => ({
    syncedFor: date,
    month: date,
    rawValue: formatDateRaw(date)
  }));

  // Sync values when date prop changes externally (set-state-in-render pattern)
  if (internal.syncedFor !== date) {
    setInternal({
      syncedFor: date,
      month: date ?? internal.month,
      rawValue: formatDateRaw(date)
    });
  }

  const { month, rawValue } = internal;
  const setMonth = (next: Date | undefined) =>
    setInternal((prev) => ({ ...prev, month: next }));
  const setRawValue = (next: string) =>
    setInternal((prev) => ({ ...prev, rawValue: next }));

  const isDateDisabled = (checkDate: Date) => {
    if (minDate && checkDate < minDate) return true;
    if (maxDate && checkDate > maxDate) return true;
    return false;
  };

  const handleSelect = (selectedDate: Date | undefined) => {
    onDateChange?.(selectedDate);
    setRawValue(formatDateRaw(selectedDate));
    if (selectedDate) {
      setOpen(false);
    }
  };

  const handleMaskedInputChange = (value: string) => {
    setRawValue(value);

    // When fully cleared, notify parent so the field is marked dirty
    if (value.length === 0) {
      onDateChange?.(undefined);
      return;
    }

    // Only parse and validate when we have a complete date
    if (value.length === 8) {
      const parsed = parseRawDate(value);
      if (parsed && !isDateDisabled(parsed)) {
        onDateChange?.(parsed);
        setMonth(parsed);
      }
    }
  };

  // Input mode: masked text field with calendar icon
  if (allowInput) {
    return (
      <div className="relative">
        <MaskedInput
          mask={MASK_PRESETS.DATE_AU}
          aria-label={ariaLabel}
          value={rawValue}
          onChange={handleMaskedInputChange}
          disabled={disabled}
          className={cn('pr-10', className)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="icon-2xs"
              type="button"
              variant="ghost"
              disabled={disabled}
              className="absolute top-1/2 right-2 -translate-y-1/2"
            >
              <CalendarIcon className="size-3.5" />
              <span className="sr-only">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout={yearNavigation ? 'dropdown' : 'label'}
              month={month}
              onMonthChange={setMonth}
              onSelect={(selectedDate) => {
                onDateChange?.(selectedDate);
                setRawValue(formatDateRaw(selectedDate));
                setOpen(false);
              }}
              disabled={isDateDisabled}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Button mode (original behavior)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-between font-normal',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          {date ? format(date, 'PPP') : <span>{placeholder}</span>}
          <ChevronDownIcon className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          captionLayout={yearNavigation ? 'dropdown' : 'label'}
          month={month}
          onMonthChange={setMonth}
          disabled={isDateDisabled}
        />
        {/*
          Inside the popover rather than beside the trigger: the trigger is a
          full-width button whose whole surface opens the calendar, so a clear
          control there would either shrink it or sit on top of it. Renders only
          when there is something to clear, so an empty optional field shows no
          control for an action that would do nothing.
        */}
        {clearable && date ? (
          <div className="flex justify-center border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              density="compact"
              tone="muted"
              onClick={() => {
                onDateChange?.(undefined);
                setOpen(false);
              }}
            >
              Clear date
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
